import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../utils/api';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  Grid, 
  Chip, 
  Card, 
  CardContent,
  IconButton,
  Alert,
  CircularProgress,
  Pagination
} from '@mui/material';
import { styled } from '@mui/material/styles';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import PersonIcon from '@mui/icons-material/Person';
import EventIcon from '@mui/icons-material/Event';
import MedicationIcon from '@mui/icons-material/Medication';

const StyledCard = styled(Card)(({ theme }) => ({
  background: 'var(--mk-surface, #ffffff)',
  color: 'var(--mk-text, #333)',
  border: '1px solid var(--mk-border, #e5e7eb)',
  borderRadius: 12,
  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
  },
}));

const PrescriptionList = ({ userRole = 'patient' }) => {
  const navigate = useNavigate();
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchPrescriptions();
  }, [page, userRole]);

  const fetchPrescriptions = async () => {
    try {
      setLoading(true);
      const endpoint = userRole === 'doctor' ? '/prescriptions/doctor' : '/prescriptions/patient';
      
      // #region agent log
      fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'PrescriptionList.js:fetchPrescriptions-start',message:'Starting prescriptions fetch',data:{endpoint,userRole,page,apiBaseUrl:api.defaults.baseURL,hostname:window.location.hostname},timestamp:Date.now(),sessionId:'debug-session',runId:'prescriptions-1',hypothesisId:'PR1'})}).catch(()=>{});
      // #endregion agent log
      
      const response = await api.get(`${endpoint}?page=${page}&limit=10`);

      // #region agent log
      const prescriptionsData = response?.data?.data?.prescriptions || [];
      const allPrescriptionsWithMedications = prescriptionsData.map(p => ({
        id: p._id,
        medicationsCount: p.medications?.length || 0,
        medications: p.medications || [],
        hasMedications: !!p.medications && Array.isArray(p.medications) && p.medications.length > 0,
        medicationsType: typeof p.medications,
        medicationsIsArray: Array.isArray(p.medications)
      }));
      const firstPrescription = prescriptionsData[0] || null;
      fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'PrescriptionList.js:fetchPrescriptions-response',message:'Prescriptions API response received',data:{success:response?.data?.success,prescriptionsCount:prescriptionsData.length,firstPrescriptionId:firstPrescription?._id,firstPrescriptionMedicationsCount:firstPrescription?.medications?.length,firstPrescriptionMedications:firstPrescription?.medications?.map(m => ({name:m?.name,dosage:m?.dosage,quantity:m?.quantity})) || [],hasMedications:!!firstPrescription?.medications,medicationsIsArray:Array.isArray(firstPrescription?.medications),status:response?.status,allPrescriptionsMedications:allPrescriptionsWithMedications},timestamp:Date.now(),sessionId:'debug-session',runId:'prescriptions-1',hypothesisId:'PR2'})}).catch(()=>{});
      // #endregion agent log

      if (response.data.success) {
        // #region agent log
        fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'PrescriptionList.js:fetchPrescriptions-setState',message:'Setting prescriptions state',data:{prescriptionsCount:response.data.data.prescriptions.length,firstPrescriptionMedicationsAfterSet:response.data.data.prescriptions[0]?.medications?.length},timestamp:Date.now(),sessionId:'debug-session',runId:'prescriptions-1',hypothesisId:'PR4'})}).catch(()=>{});
        // #endregion agent log
        setPrescriptions(response.data.data.prescriptions);
        setTotalPages(response.data.data.pagination.totalPages);
      } else {
        setError(response.data.message || 'Failed to fetch prescriptions');
      }
    } catch (err) {
      // #region agent log
      const errorData = {
        message: err?.message,
        responseStatus: err?.response?.status,
        responseData: typeof err?.response?.data === 'string' ? err?.response?.data?.substring(0, 200) : err?.response?.data,
        responseText: typeof err?.response?.data === 'string' ? err?.response?.data?.substring(0, 200) : null,
        isHtmlResponse: typeof err?.response?.data === 'string' && err?.response?.data?.includes('<!doctype'),
        configUrl: err?.config?.url,
        configBaseURL: err?.config?.baseURL
      };
      fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'PrescriptionList.js:fetchPrescriptions-error',message:'Prescriptions fetch error',data:errorData,timestamp:Date.now(),sessionId:'debug-session',runId:'prescriptions-1',hypothesisId:'PR3'})}).catch(()=>{});
      // #endregion agent log
      console.error('Error fetching prescriptions:', err);
      setError('Failed to fetch prescriptions');
    } finally {
      setLoading(false);
    }
  };


  const handlePageChange = (event, value) => {
    setPage(value);
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '50vh'
      }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        px: { xs: 1, sm: 2, md: 0 }
      }}
    >
      {/* Header */}
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          mb: { xs: 2, sm: 2.5, md: 3 },
          flexWrap: 'wrap',
          gap: 1
        }}
      >
        <LocalHospitalIcon 
          sx={{ 
            mr: { xs: 1, sm: 2 }, 
            color: 'var(--mk-primary, #3b82f6)',
            fontSize: { xs: '1.5rem', sm: '2rem' }
          }} 
        />
        <Typography 
          variant="h5" 
          sx={{ 
            color: 'var(--mk-text, #333)',
            fontWeight: 600,
            fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' }
          }}
        >
          {userRole === 'doctor' ? 'My Prescriptions' : 'My Prescriptions'}
        </Typography>
      </Box>

      {prescriptions.length === 0 ? (
        <StyledCard>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <LocalHospitalIcon sx={{ fontSize: 64, color: 'var(--mk-text-secondary, #6b7280)', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              No Prescriptions Found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {userRole === 'doctor' 
                ? 'You haven\'t created any prescriptions yet.' 
                : 'You don\'t have any prescriptions yet.'
              }
            </Typography>
          </CardContent>
        </StyledCard>
      ) : (
        <>
          {/* Prescriptions Grid */}
          <Grid 
            container 
            spacing={{ xs: 2, sm: 2, md: 3 }}
            sx={{
              width: '100%',
              margin: 0
            }}
          >
            {prescriptions.map((prescription) => {
              // #region agent log
              const medicationsCount = prescription.medications?.length || 0;
              const hasMedications = !!prescription.medications && Array.isArray(prescription.medications) && medicationsCount > 0;
              if (prescriptions.indexOf(prescription) === 0) {
                fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'PrescriptionList.js:render-prescription',message:'Rendering prescription card',data:{prescriptionId:prescription._id,medicationsCount,hasMedications,medicationsType:typeof prescription.medications,medicationsIsArray:Array.isArray(prescription.medications),medicationsSample:prescription.medications?.slice(0,2).map(m => ({name:m?.name,dosage:m?.dosage})) || []},timestamp:Date.now(),sessionId:'debug-session',runId:'prescriptions-1',hypothesisId:'PR5'})}).catch(()=>{});
              }
              // #endregion agent log
              
              return (
              <Grid 
                item 
                xs={12} 
                sm={6} 
                md={6} 
                lg={4} 
                xl={3}
                key={prescription._id}
                sx={{
                  display: 'flex',
                  minWidth: 0
                }}
              >
                <StyledCard
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%'
                  }}
                >
                  <CardContent
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      p: { xs: 2, sm: 2.5, md: 3 }
                    }}
                  >
                    {/* Header */}
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'flex-start', 
                        mb: { xs: 1.5, sm: 2 },
                        flexWrap: 'wrap',
                        gap: 1
                      }}
                    >
                      <Chip 
                        label={prescription.prescriptionId}
                        size="small"
                        variant="outlined"
                        sx={{ 
                          borderColor: 'var(--mk-primary, #3b82f6)',
                          color: 'var(--mk-primary, #3b82f6)',
                          fontSize: { xs: '0.7rem', sm: '0.75rem' },
                          height: { xs: 24, sm: 28 }
                        }}
                      />
                      <Chip 
                        label={prescription.status}
                        size="small"
                        color={prescription.status === 'active' ? 'success' : 'default'}
                        sx={{
                          fontSize: { xs: '0.7rem', sm: '0.75rem' },
                          height: { xs: 24, sm: 28 }
                        }}
                      />
                    </Box>

                    {/* Patient/Doctor Info */}
                    <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          mb: 0.5,
                          fontSize: { xs: '0.75rem', sm: '0.875rem' }
                        }}
                      >
                        <PersonIcon sx={{ fontSize: { xs: 14, sm: 16 }, mr: 0.5 }} />
                        {userRole === 'doctor' ? 'Patient' : 'Doctor'}
                      </Typography>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          fontWeight: 500,
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          wordBreak: 'break-word'
                        }}
                      >
                        {userRole === 'doctor' 
                          ? prescription.patientName 
                          : prescription.doctorName
                        }
                      </Typography>
                    </Box>

                    {/* Date */}
                    <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          mb: 0.5,
                          fontSize: { xs: '0.75rem', sm: '0.875rem' }
                        }}
                      >
                        <EventIcon sx={{ fontSize: { xs: 14, sm: 16 }, mr: 0.5 }} />
                        Prescription Date
                      </Typography>
                      <Typography 
                        variant="body1"
                        sx={{
                          fontSize: { xs: '0.875rem', sm: '1rem' }
                        }}
                      >
                        {new Date(prescription.date).toLocaleDateString()}
                      </Typography>
                    </Box>

                    {/* Medications Count */}
                    <Box sx={{ mb: { xs: 1.5, sm: 2 }, flex: 1 }}>
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          mb: 0.5,
                          fontSize: { xs: '0.75rem', sm: '0.875rem' }
                        }}
                      >
                        <MedicationIcon sx={{ fontSize: { xs: 14, sm: 16 }, mr: 0.5 }} />
                        Medications
                      </Typography>
                      <Typography 
                        variant="body1"
                        sx={{
                          fontSize: { xs: '0.875rem', sm: '1rem' }
                        }}
                      >
                        {medicationsCount} medication(s)
                      </Typography>
                      {/* Display medications list if available */}
                      {hasMedications && (
                        <Box sx={{ mt: 1, pl: { xs: 1.5, sm: 2 } }}>
                          {prescription.medications.slice(0, 3).map((med, idx) => (
                            <Typography 
                              key={idx} 
                              variant="body2" 
                              color="text.secondary" 
                              sx={{ 
                                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                wordBreak: 'break-word',
                                mb: 0.25
                              }}
                            >
                              • {med.name} {med.dosage && `- ${med.dosage}`}
                            </Typography>
                          ))}
                          {medicationsCount > 3 && (
                            <Typography 
                              variant="body2" 
                              color="text.secondary" 
                              sx={{ 
                                fontSize: { xs: '0.7rem', sm: '0.75rem' }, 
                                fontStyle: 'italic' 
                              }}
                            >
                              +{medicationsCount - 3} more
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>

                    {/* Actions */}
                    <Box sx={{ mt: 2 }}>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<VisibilityIcon />}
                        onClick={() => navigate(`/prescription/${prescription._id}`)}
                        fullWidth
                        sx={{
                          backgroundColor: 'var(--mk-primary, #3b82f6)',
                          color: 'white',
                          '&:hover': {
                            backgroundColor: 'var(--mk-primary-dark, #2563eb)'
                          },
                          py: 1,
                          fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                          fontWeight: 600
                        }}
                      >
                        View Details
                      </Button>
                    </Box>
                  </CardContent>
                </StyledCard>
              </Grid>
              );
            })}
          </Grid>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box 
              sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                mt: { xs: 3, sm: 4 },
                px: { xs: 1, sm: 0 }
              }}
            >
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size={{ xs: 'small', sm: 'medium' }}
                sx={{
                  '& .MuiPaginationItem-root': {
                    color: 'var(--mk-text, #333)',
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  },
                  '& .Mui-selected': {
                    backgroundColor: 'var(--mk-primary, #3b82f6)',
                    color: 'white',
                  },
                }}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default PrescriptionList;
